import React from 'react'
import { Col, List, Modal, Radio, Row, Typography } from 'antd'
import { Formik, FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

import { Input } from 'src/components/Formik'
import { PolicyFlowClosing, PolicyFlowFormat, PolicyFlowType } from 'src/types'

import * as CSS from './styles'
import * as Values from './initial-values'

interface Props {
  disabled?: boolean
  formikProps: FormikProps<PolicyFlowClosing>
  isLoading?: boolean
  isVisible: boolean
  modalPolicyFlowFormat: PolicyFlowFormat
  modalPolicyFlowType: PolicyFlowType
  refFormik: React.MutableRefObject<Formik<PolicyFlowClosing>>
  setModalPolicyFlowFormat?: React.Dispatch<
    React.SetStateAction<PolicyFlowFormat>
  >
  setModalPolicyFlowType?: React.Dispatch<React.SetStateAction<PolicyFlowType>>
}

export default function FlowClosingsModal({
  disabled = false,
  formikProps,
  isLoading = false,
  isVisible,
  modalPolicyFlowFormat,
  modalPolicyFlowType,
  refFormik,
  setModalPolicyFlowFormat,
  setModalPolicyFlowType
}: Props) {
  const { t } = useTranslation()

  const policyFlowType = [
    {
      description: t('web.policies-flow.closing.type.hierarchy-description'),
      title: t('web.policies-flow.type.hierarchy'),
      value: PolicyFlowType.HIERARCHY
    },
    {
      description: t('web.policies-flow.closing.type.cost-center-description'),
      title: t('web.policies-flow.type.cost-center'),
      value: PolicyFlowType.COST_CENTER
    }
  ]

  const policyFlowFormat = [
    {
      description: t('web.policies-flow.closing.format.standard-description'),
      title: t('web.policies-flow.format.standard'),
      value: PolicyFlowFormat.STANDARD
    },
    {
      description: t('web.policies-flow.closing.format.automatic-description'),
      title: t('web.policies-flow.format.automatic'),
      value: PolicyFlowFormat.AUTOMATIC
    },
    {
      description: t('web.policies-flow.closing.format.consensus-description'),
      title: t('web.policies-flow.format.consensus'),
      value: PolicyFlowFormat.CONSENSUS
    }
  ]

  const title = t('web.policies-flow.closing.modal-title')

  return (
    <Modal
      cancelButtonProps={{ loading: isLoading }}
      centered
      closable
      destroyOnClose
      okButtonProps={{
        loading: isLoading,
        hidden: disabled
      }}
      onCancel={() => {
        refFormik.current?.resetForm(Values.initialValues)
      }}
      onOk={(e: any) => {
        refFormik.current?.handleSubmit(e)
      }}
      title={title}
      visible={isVisible}
      width={800}
    >
      <Row gutter={24}>
        <Col span={6}>
          <Input
            data-test-id="modal-closing-code"
            disabled={disabled}
            formikProps={formikProps}
            label="web.tree.node.code"
            name="code"
            placeholder="web.tree.node.code-placeholder"
          />
        </Col>
        <Col span={18}>
          <Input
            data-test-id="modal-closing-name"
            disabled={disabled}
            formikProps={formikProps}
            label="web.tree.node.name"
            name="name"
            placeholder="web.tree.node.name-placeholder"
          />
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <Input
            data-test-id="modal-closing-description"
            disabled={disabled}
            formikProps={formikProps}
            label="web.resources.common.description.title"
            name="description"
            placeholder="web.resources.common.description.placeholder"
            required={false}
          />
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.closing.type.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Radio.Group
            defaultValue={PolicyFlowType.HIERARCHY}
            disabled={disabled}
            style={{ width: '100%' }}
            value={modalPolicyFlowType}
          >
            <List
              data-test-id="modal-closing-type"
              dataSource={policyFlowType}
              itemLayout="horizontal"
              renderItem={item => (
                <Row
                  gutter={[2, 2]}
                  onClick={() => {
                    if (!disabled) {
                      setModalPolicyFlowType(item.value)
                      let cost_center_closing = false
                      let reason_closing = false
                      switch (item.value) {
                        case PolicyFlowType.COST_CENTER:
                          cost_center_closing = true
                          reason_closing = false
                          break
                        case PolicyFlowType.REASON:
                          cost_center_closing = false
                          reason_closing = true
                      }

                      formikProps.setFieldValue(
                        'cost_center_closing',
                        cost_center_closing
                      )
                      formikProps.setFieldValue(
                        'reason_closing',
                        reason_closing
                      )
                    }
                  }}
                >
                  <Col span={5}>
                    <Typography.Text strong>{item.title}</Typography.Text>
                  </Col>
                  <Col span={1}>
                    <Radio value={item.value} />
                  </Col>
                  <Col span={18}>
                    <CSS.ModalDescription>
                      {item.description}
                    </CSS.ModalDescription>
                  </Col>
                </Row>
              )}
            />
          </Radio.Group>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={24}>
          <CSS.ModalTitle>
            {t('web.policies-flow.closing.format.title')}
          </CSS.ModalTitle>
        </Col>
      </Row>
      <Row align="middle" gutter={[24, 24]} justify="space-between">
        <Col span={24}>
          <Radio.Group
            defaultValue={PolicyFlowFormat.STANDARD}
            disabled={disabled}
            style={{ width: '100%' }}
            value={modalPolicyFlowFormat}
          >
            <List
              data-test-id="modal-closing-format"
              dataSource={policyFlowFormat}
              itemLayout="horizontal"
              renderItem={item => (
                <Row
                  gutter={[2, 2]}
                  onClick={() => {
                    if (!disabled) {
                      setModalPolicyFlowFormat(item.value)
                      let automatic_closing = false
                      let consensual_closing = false
                      switch (item.value) {
                        case PolicyFlowFormat.AUTOMATIC:
                          automatic_closing = true
                          consensual_closing = false
                          break
                        case PolicyFlowFormat.CONSENSUS:
                          automatic_closing = false
                          consensual_closing = true
                      }

                      formikProps.setFieldValue(
                        'automatic_closing',
                        automatic_closing
                      )
                      formikProps.setFieldValue(
                        'consensual_closing',
                        consensual_closing
                      )
                    }
                  }}
                >
                  <Col span={5}>
                    <Typography.Text strong>{item.title}</Typography.Text>
                  </Col>
                  <Col span={1}>
                    <Radio value={item.value} />
                  </Col>
                  <Col span={18}>
                    <CSS.ModalDescription>
                      {item.description}
                    </CSS.ModalDescription>
                  </Col>
                </Row>
              )}
            />
          </Radio.Group>
        </Col>
      </Row>
    </Modal>
  )
}
